import React, { useEffect, useMemo, useState } from 'react';
import MainLayout from '../components/Layout/MainLayout';
import adminService from '../services/adminService';

const EMPTY_IP = '';

const AdminSecurityPage = () => {
  const [officeIps, setOfficeIps] = useState([EMPTY_IP]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [hrUsers, setHrUsers] = useState([]);
  const [loadingHrUsers, setLoadingHrUsers] = useState(false);
  const [resetUserId, setResetUserId] = useState('');
  const [resetting, setResetting] = useState(false);
  const [resetMessage, setResetMessage] = useState('');

  useEffect(() => {
    loadOfficeIps();
    loadHrUsers();
  }, []);

  const normalizedIps = useMemo(() => {
    return officeIps.map((ip) => ip.trim()).filter(Boolean);
  }, [officeIps]);

  const loadOfficeIps = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await adminService.getOfficeIps();
      const list = Array.isArray(response?.data?.officeIps) ? response.data.officeIps : [];
      setOfficeIps(list.length ? list : [EMPTY_IP]);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load office IP list');
    } finally {
      setLoading(false);
    }
  };

  const loadHrUsers = async () => {
    setLoadingHrUsers(true);
    try {
      const response = await adminService.getHRUsers();
      if (response?.success && Array.isArray(response.data)) {
        setHrUsers(response.data);
      }
    } catch (err) {
      console.error('Failed to load HR users:', err);
    } finally {
      setLoadingHrUsers(false);
    }
  };

  const updateIpAt = (index, value) => {
    setOfficeIps((prev) => prev.map((item, currentIndex) => (currentIndex === index ? value : item)));
  };

  const addIpRow = () => {
    setOfficeIps((prev) => [...prev, EMPTY_IP]);
  };

  const removeIpRow = (index) => {
    setOfficeIps((prev) => {
      const next = prev.filter((_, currentIndex) => currentIndex !== index);
      return next.length ? next : [EMPTY_IP];
    });
  };

  const saveOfficeIps = async () => {
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const response = await adminService.updateOfficeIps(normalizedIps);
      const savedList = Array.isArray(response?.data?.officeIps) ? response.data.officeIps : normalizedIps;
      setOfficeIps(savedList.length ? savedList : [EMPTY_IP]);
      setSuccess(response?.message || 'Office IP list updated successfully');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save office IP list');
    } finally {
      setSaving(false);
    }
  };

  const resetDeviceBinding = async (event) => {
    event.preventDefault();
    setResetMessage('');

    if (!resetUserId.trim()) {
      setResetMessage('User ID is required');
      return;
    }

    setResetting(true);

    try {
      const response = await adminService.resetUserDeviceBinding(resetUserId.trim());
      setResetMessage(response?.message || 'Device binding reset successfully');
      setResetUserId('');
    } catch (err) {
      setResetMessage(err.response?.data?.error || 'Failed to reset device binding');
    } finally {
      setResetting(false);
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="mx-auto max-w-4xl p-6">
          <div className="rounded-lg bg-white p-6 shadow-sm">Loading security settings...</div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="mx-auto max-w-4xl space-y-6 p-6">
        <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
          <h1 className="text-2xl font-semibold text-gray-900">Security Controls</h1>
          <p className="mt-2 text-sm text-gray-600">
            Manage office public IP allowlist for HR/HRO login and reset bound device when needed.
          </p>
        </div>

        <section className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
          <div className="mb-4 flex items-center justify-between gap-3">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Office IP Allowlist</h2>
              <p className="mt-1 text-sm text-gray-600">Only these public IPs can be used for HR/HRO login.</p>
            </div>
            <button
              type="button"
              className="rounded-md bg-gray-100 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200"
              onClick={addIpRow}
            >
              Add IP
            </button>
          </div>

          <div className="space-y-3">
            {officeIps.map((ip, index) => (
              <div key={`${index}-${ip}`} className="flex items-center gap-2">
                <input
                  type="text"
                  value={ip}
                  onChange={(event) => updateIpAt(index, event.target.value)}
                  placeholder="e.g. 49.207.120.10"
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-600 focus:outline-none"
                />
                <button
                  type="button"
                  className="rounded-md border border-red-200 px-3 py-2 text-sm text-red-600 hover:bg-red-50"
                  onClick={() => removeIpRow(index)}
                >
                  Remove
                </button>
              </div>
            ))}
          </div>

          {error && <p className="mt-4 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}
          {success && <p className="mt-4 rounded-md bg-green-50 px-3 py-2 text-sm text-green-700">{success}</p>}

          <div className="mt-5 flex justify-end">
            <button
              type="button"
              disabled={saving}
              className="rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
              onClick={saveOfficeIps}
            >
              {saving ? 'Saving...' : 'Save Office IPs'}
            </button>
          </div>
        </section>

        <section className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
          <h2 className="text-xl font-semibold text-gray-900">Reset HR/HRO Device Binding</h2>
          <p className="mt-1 text-sm text-gray-600">
            Select HR/HRO user and reset bound device so that user can login from a new device.
          </p>

          <form className="mt-4 space-y-3" onSubmit={resetDeviceBinding}>
            <select
              value={resetUserId}
              onChange={(event) => setResetUserId(event.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-600 focus:outline-none"
              disabled={loadingHrUsers}
            >
              <option value="">Select HR/HRO User</option>
              {hrUsers.map((user) => (
                <option key={user._id} value={user._id}>
                  {user.name} ({user.employeeId || user.phoneNumber}) - {user.role}
                </option>
              ))}
            </select>
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={resetting || !resetUserId}
                className="rounded-md bg-amber-600 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {resetting ? 'Resetting...' : 'Reset Device'}
              </button>
            </div>
          </form>

          {resetMessage && (
            <p className="mt-3 rounded-md bg-slate-50 px-3 py-2 text-sm text-slate-700">{resetMessage}</p>
          )}
        </section>
      </div>
    </MainLayout>
  );
};

export default AdminSecurityPage;
